/* Session state.

   The user object is small, read on almost every screen, and must be correct
   the moment the first frame paints — a header that renders "Sign in" and then
   swaps to a name a beat later is the kind of flicker that reads as a broken
   app. So it is hydrated synchronously from storage rather than fetched.

   `subscribe` keeps tabs honest: signing out in one tab must not leave another
   rendering an account page for a user who is gone. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { endSession, startSession } from '../lib/session'
import { setTelemetryUser } from '../lib/telemetry'
import { signOut as revokeSession } from '../lib/api'
import { load, remove as removeKey, save, subscribe } from '../lib/storage'
import type { AuthSession, User } from '../lib/types'

interface AuthApi {
  user: User | null
  isAuthed: boolean
  /** Takes the whole session, not just the user: the token has to reach
   *  `lib/session.ts` or every authenticated request goes out unsigned, and
   *  making that one call the only way in is what stops the two drifting. */
  signIn: (session: AuthSession) => void
  signOut: () => void
  patch: (fields: Partial<User>) => void
}

const AuthContext = createContext<AuthApi | null>(null)

const KEY = 'user'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => load<User | null>(KEY, null))

  useEffect(() => subscribe(KEY, () => setUser(load<User | null>(KEY, null))), [])

  /* Mirrored into telemetry from one effect rather than from `signIn` and
     `signOut` separately, because there is a third way to arrive here that
     neither of those covers: a returning visitor whose user was restored from
     localStorage before this component ever rendered. Reports from that
     session — much the commonest kind — would otherwise carry no account at
     all, which is precisely the situation where "is this only affecting one
     person" cannot be answered.

     The account id, never the number. It means nothing outside our database
     and it answers the only question a report needs it for. */
  useEffect(() => {
    setTelemetryUser(user?.id ?? null)
  }, [user])

  const signIn = useCallback((session: AuthSession) => {
    startSession(session)
    save(KEY, session.user)
    setUser(session.user)
  }, [])

  const signOut = useCallback(() => {
    /* Told to the server, not merely forgotten here. The refresh token is an
       HttpOnly cookie this app cannot see or delete, so clearing localStorage
       alone leaves a live credential in the jar of what is very often a shared
       family phone — and it stays live for as long as a refresh token lasts.
       Only the service can revoke it.

       Fired without being waited on, and its failure is swallowed on purpose.
       Signing out is a thing the user asked for and it has to happen on this
       device whatever the network says: a sign-out that could fail is a
       sign-out button that sometimes leaves you signed in. The cookie then
       outlives its access token and dies on its own. */
    void revokeSession().catch(() => undefined)

    endSession()
    removeKey(KEY)
    setUser(null)
    /* Deliberately not clearing the basket. Signing out of a shared device
       should not throw away work in progress, and the basket holds nothing
       identifying — it is item ids and quantities. Addresses and orders are
       keyed to the user and are re-read on the next sign-in. */
  }, [])

  const patch = useCallback((fields: Partial<User>) => {
    setUser((current) => {
      if (!current) return current
      const next = { ...current, ...fields }
      save(KEY, next)
      return next
    })
  }, [])

  const value = useMemo<AuthApi>(
    () => ({ user, isAuthed: user !== null, signIn, signOut, patch }),
    [user, signIn, signOut, patch],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
